import { Component, input, output } from '@angular/core';
import { LucideChevronRight } from '@lucide/angular';
import { TranslateModule } from '@ngx-translate/core';
import { PmLinkComponent } from '@libs/ui/buttons';
import { PmIconComponent } from '@libs/ui/icon';

export interface PmBreadcrumb {
  label: string;
  clickable?: boolean;
}

export interface PmBreadcrumbClickEvent {
  item: PmBreadcrumb;
  index: number;
}

@Component({
  selector: 'pm-breadcrumbs',
  imports: [PmIconComponent, PmLinkComponent, TranslateModule],
  template: `
    <nav>
      <ol class="flex flex-row gap-1 items-center m-0 p-0 list-none">
        @let breadcrumbs = items();

        @for (breadcrumb of breadcrumbs; track breadcrumb) {
          @let isClickable = !$last && (breadcrumb.clickable ?? false);
          <li class="flex flex-row gap-1 items-center">
            @if (isClickable) {
              <pm-link
                size="large"
                [text]="breadcrumb.label"
                (handleClick)="handleClick(breadcrumb, $index)"
              />
            } @else {
              <span
                class="text-sm leading-body text-content-body-text"
                [attr.aria-current]="$last ? 'page' : null"
              >
                {{ breadcrumb.label | translate }}
              </span>
            }
            @if (!$last) {
              <pm-icon
                size="sm"
                [icon]="separatorIcon"
                class="text-content-body-text"
                aria-hidden="true"
              />
            }
          </li>
        }
      </ol>
    </nav>
  `,
})
export class PmBreadcrumbsComponent {
  items = input.required<PmBreadcrumb[]>();
  breadcrumbClick = output<PmBreadcrumbClickEvent>();

  protected readonly separatorIcon = LucideChevronRight;

  protected handleClick(item: PmBreadcrumb, index: number): void {
    const isLast = index === this.items().length - 1;
    if (isLast || !item.clickable) {
      return;
    }
    this.breadcrumbClick.emit({ item, index });
  }
}
